import { useState } from "react";
import { theme, btnPrimary, type as text } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { secondaryBtn, input as inputStyle } from "../pages/resilience/shared";

// Bulk triage on the Threat Intelligence page: one status and one note for every selected
// advisory (POST /threat-feeds/triage/bulk, risks:write). A linked register risk already on an
// advisory is kept; closing (actioned / not applicable) needs a note, as the single editor does.

export type BulkTriageStatus = "acknowledged" | "assessing" | "actioned" | "not_applicable";

const STATUS_LABELS: Record<BulkTriageStatus, string> = {
  acknowledged: "Acknowledged",
  assessing: "Assessing",
  actioned: "Actioned",
  not_applicable: "Not applicable",
};

export function ThreatBulkTriage<T>({ selected, onClear, onSelectAll, pageCount, onDone }: {
  selected: string[];
  onClear: () => void;
  onSelectAll: () => void;
  pageCount: number;
  onDone: (triage: Record<string, T>) => void;
}) {
  const [status, setStatus] = useState<BulkTriageStatus>("acknowledged");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (selected.length === 0) return null;
  const closing = status === "actioned" || status === "not_applicable";

  async function apply() {
    if (closing && !note.trim()) { setError(status === "actioned" ? "Say what was done." : "Say why they do not apply."); return; }
    setBusy(true); setError(null);
    try {
      const res = await api.post<{ updated: number; triage: Record<string, T> }>("/threat-feeds/triage/bulk", { itemIds: selected, status, note: note.trim() || null });
      setNote("");
      onDone(res.triage);
    } catch (e) {
      setError(apiErrorMessage(e, "Could not save the triage"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div role="region" aria-label="Bulk triage" style={{
      position: "sticky", top: 8, zIndex: 5, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center",
      padding: "10px 14px", marginBottom: 12, borderRadius: theme.radius.md,
      border: `1px solid ${theme.color.accent}`, background: theme.color.accentBg, fontFamily: theme.font,
    }}>
      <span style={{ ...text.body, fontWeight: 600, color: theme.color.accentText }}>{selected.length} selected</span>
      {selected.length < pageCount && (
        <button type="button" onClick={onSelectAll} style={{ ...secondaryBtn, padding: "4px 10px", fontSize: text.hint.fontSize }}>Select all {pageCount} on this page</button>
      )}
      <button type="button" onClick={onClear} style={{ ...secondaryBtn, padding: "4px 10px", fontSize: text.hint.fontSize }}>Clear</button>
      <span style={{ flex: "0 0 8px" }} />
      <label style={{ ...text.hint, color: theme.color.text, display: "flex", alignItems: "center", gap: 6 }}>
        Set to
        <select value={status} onChange={(e) => setStatus(e.target.value as BulkTriageStatus)} style={{ ...inputStyle, width: "auto", padding: "5px 8px" }}>
          {(Object.keys(STATUS_LABELS) as BulkTriageStatus[]).map((k) => <option key={k} value={k}>{STATUS_LABELS[k]}</option>)}
        </select>
      </label>
      <input
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder={closing ? (status === "actioned" ? "What was done (required)" : "Why they do not apply (required)") : "Note (optional)"}
        aria-label="Triage note"
        style={{ ...inputStyle, flex: "1 1 220px", minWidth: 180, padding: "5px 8px" }}
      />
      <button type="button" disabled={busy} onClick={apply} style={{ ...btnPrimary, padding: "6px 14px", fontSize: text.hint.fontSize, opacity: busy ? 0.6 : 1 }}>
        {busy ? "Saving…" : `Apply to ${selected.length}`}
      </button>
      {error && <div style={{ ...text.hint, flexBasis: "100%", color: theme.color.dangerText }}>{error}</div>}
    </div>
  );
}
